import { AxiosResponse } from 'axios';
import api from './axiosConfig';
import { invalidando } from './queryClient';
import { CHAVES_PROGRESSO } from './ProgressService';
import { CHAVES_REACOES } from './StepReactionService';
import { ProgressLog } from '../types/entities';
import { LogType } from '../types/enums';
import { Page } from '../types';

/**
 * `lista` inclui o tipo na chave: SKIN, ROUTINE e "todos" são resultados
 * diferentes do mesmo endpoint — o filtro é identidade do dado, não detalhe.
 */
export const CHAVES_LOGS = {
  dominio: ['logs'] as const,
  lista: (userId: string, type?: LogType) => ['logs', 'lista', userId, type ?? 'TODOS'] as const,
  fotosComparacao: (userId: string) => ['logs', 'fotos-comparacao', userId] as const,
  /** Galeria de fotos, paginada (o usePagina põe a página no fim da chave). */
  galeria: (userId: string) => ['logs', 'galeria', userId] as const,
};

const ProgressLogService = {
  // POST /api/v1/users/{userId}/logs
  // Um registro novo mexe em streak e insights também.
  createLog: (userId: string, logData: Partial<ProgressLog>): Promise<AxiosResponse<ProgressLog>> =>
    api
      .post(`/users/${userId}/logs`, logData)
      .then(invalidando(CHAVES_LOGS.dominio, CHAVES_PROGRESSO.dominio)),

  // GET /api/v1/users/{userId}/logs[?type=SKIN|ROUTINE]
  // Retorna os logs já filtrados por tipo e ordenados (mais recentes primeiro) pelo backend.
  getLogs: (userId: string, type?: LogType): Promise<AxiosResponse<ProgressLog[]>> =>
    api.get(`/users/${userId}/logs`, type ? { params: { type } } : undefined),

  // GET /api/v1/users/{userId}/logs?type=&page= → Page<ProgressLog>
  // A listagem das telas de histórico: 10 por página, mais recentes primeiro.
  getLogPage: (userId: string, type: LogType, page: number): Promise<AxiosResponse<Page<ProgressLog>>> =>
    api.get(`/users/${userId}/logs`, { params: { type, page } }),

  // GET /api/v1/users/{userId}/logs/skin-photos/comparison?page=&sort=logDate,id,desc → Page<ProgressLog>
  // A galeria: só registros com foto, 10 por página, mais recentes primeiro (o id
  // desempata o mesmo dia, senão uma foto podia repetir ou sumir entre as páginas).
  getSkinPhotoPage: (userId: string, page: number): Promise<AxiosResponse<Page<ProgressLog>>> =>
    api.get(`/users/${userId}/logs/skin-photos/comparison`, { params: { page, sort: 'logDate,id,desc' } }),

  // GET /api/v1/users/{userId}/logs/skin-photos/comparison
  // Retorna fotos de pele ordenadas do mais antigo para o mais recente
  getSkinPhotosForComparison: (userId: string): Promise<AxiosResponse<ProgressLog[]>> =>
    api.get(`/users/${userId}/logs/skin-photos/comparison`),

  // DELETE /api/v1/users/{userId}/logs/{logId}
  // Remove o registro e, no backend, o arquivo de foto associado (se houver) —
  // junto vão as reações daquele log e os números de progresso.
  deleteLog: (userId: string, logId: string): Promise<AxiosResponse<void>> =>
    api
      .delete(`/users/${userId}/logs/${logId}`)
      .then(invalidando(CHAVES_LOGS.dominio, CHAVES_PROGRESSO.dominio, CHAVES_REACOES.dominio)),
};

export default ProgressLogService;
